/**
 * How far either side of spot the payoff is drawn — ROADMAP 9.46.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * A CLOSED SET OF STOPS, NOT A NUMBER BETWEEN TWO BOUNDS
 *
 * The control was `min={2} max={12} step={1}`, which is three decisions
 * (bounds and granularity) encoded as three magic numbers at the call site,
 * and a type that admits 7.5 and -3 as readily as 4. It also could not
 * express a tenth of a percent, which is what reading an expiry-day position
 * around its breakeven actually needs: on NIFTY at 23,000, ±2% is ±460
 * points and the whole decision lives inside ±50.
 *
 * So the stops are a tuple and the value is a union over it. Every reachable
 * zoom is a member of `ZOOM_STEPS_PCT`, the compiler knows the whole set, and
 * adding 0.05% later is one edit in one place rather than a bounds change
 * that silently also permits 0.06.
 *
 * THE SCALE IS NON-LINEAR ON PURPOSE. Equal slider travel buys roughly equal
 * RATIO, because that is how zoom is read: 0.1 → 0.2 is the same move as
 * 10 → 20. A linear 0.1-step slider from 0.1 to 20 would be 200 positions,
 * 190 of which nobody wants.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * FIT IS A MODE, NOT A WIDTH
 *
 * `AUTO` is not "some particular percentage". It means *derive the window
 * from the position* — `payoffWindow` covers every strike plus a margin — so
 * the answer changes with the structure and cannot be written as a stop.
 *
 * It is the default because the alternative failed in the obvious way: at a
 * fixed ±2% a Bull Put Spread's max-profit plateau ran off the left edge, so
 * the chart could not show where the position stops making money, which is
 * the one thing a payoff chart exists to show.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { payoffWindow, type PayoffWindow } from './payoffView'
import type { PayoffLeg } from '@apna/shared'

/**
 * The reachable half-spans, as a percentage of spot, ascending.
 *
 * Ascending is load-bearing: the slider maps travel to index, so the order
 * here IS the order on screen.
 */
export const ZOOM_STEPS_PCT = [
  0.1, 0.2, 0.3, 0.5, 0.75, 1, 1.5, 2, 3, 4, 5, 6, 8, 10, 12, 15, 20,
] as const

export type ZoomPct = (typeof ZOOM_STEPS_PCT)[number]

/**
 * What the reader has asked the x-axis to show.
 *
 * A discriminated union rather than `number | null`, so "fit the position"
 * and "show ±4%" are different KINDS rather than one of them being a
 * sentinel value of the other. `zoom.pct` is unreachable without narrowing,
 * which is what stops a caller reading a width out of fit mode.
 */
export type PayoffZoom =
  | { readonly kind: 'AUTO' }
  | { readonly kind: 'SPAN'; readonly pct: ZoomPct }

export const FIT: PayoffZoom = { kind: 'AUTO' }

export const spanZoom = (pct: ZoomPct): PayoffZoom => ({ kind: 'SPAN', pct })

/** The last reachable index, for the slider's `max`. */
export const MAX_ZOOM_SLOT = ZOOM_STEPS_PCT.length - 1

/**
 * The stop at a slider position, clamped.
 *
 * Clamped rather than trusted: the value arrives from an `<input type=range>`
 * as a string, and a non-integer or an out-of-bounds index would index past
 * the tuple and hand `undefined` to arithmetic that reports NaN as a window.
 */
export function zoomAtSlot(slot: number): PayoffZoom {
  const i = Math.min(MAX_ZOOM_SLOT, Math.max(0, Math.round(slot)))
  return spanZoom(ZOOM_STEPS_PCT[i]!)
}

/**
 * Where the slider should sit for a zoom.
 *
 * In FIT mode it sits at the stop NEAREST what fit resolved to, so taking
 * manual control moves the view as little as possible — a slider that jumped
 * to one end the moment you touched it would make fit mode a trap.
 */
export function slotOfZoom(zoom: PayoffZoom, resolvedPct: number | null): number {
  const target = zoom.kind === 'SPAN' ? zoom.pct : resolvedPct
  if (target === null) return ZOOM_STEPS_PCT.indexOf(4 as ZoomPct)
  let best = 0
  let bestGap = Infinity
  for (let i = 0; i < ZOOM_STEPS_PCT.length; i += 1) {
    const gap = Math.abs(ZOOM_STEPS_PCT[i]! - target)
    if (gap < bestGap) {
      bestGap = gap
      best = i
    }
  }
  return best
}

/**
 * A half-span as it reads on screen.
 *
 * Two decimals below one percent and none above, because "±0.10%" and "±4%"
 * are each the shortest form that is not ambiguous — "±0.1%" invites being
 * read as a truncation, and "±4.00%" is three characters of noise in a row
 * that must not reflow as the number changes.
 */
export function formatHalfPct(pct: number): string {
  if (pct < 1) return `±${pct.toFixed(2)}%`
  return `±${Number.isInteger(pct) ? pct : pct.toFixed(1)}%`
}

/** What the control says it is doing right now. */
export function zoomLabel(zoom: PayoffZoom, resolvedPct: number | null): string {
  if (zoom.kind === 'SPAN') return formatHalfPct(zoom.pct)
  return resolvedPct === null ? 'Fit' : `Fit ${formatHalfPct(resolvedPct)}`
}

/**
 * The window to draw, for either mode.
 *
 * ONE window, returned to ONE caller, which then hands the same object to
 * the graph and to the heat table. They used to derive it twice — the chart
 * from a `window` prop and the table from a `rangePct` number — which is two
 * chances to disagree about what "the displayed range" means, on two views
 * the reader switches between expecting to see the same thing.
 */
export function zoomWindow(
  zoom: PayoffZoom,
  spot: number,
  legs: readonly PayoffLeg[],
): PayoffWindow {
  if (zoom.kind === 'AUTO') return payoffWindow(legs, spot > 0 ? spot : null)
  const half = spot * (zoom.pct / 100)
  return { from: Math.max(0, spot - half), to: spot + half }
}

/**
 * The window expressed back as a half-percentage of spot.
 *
 * The heat table steps rows by a percentage rather than by a price, so fit
 * mode has to be translated into its units. Derived FROM the window rather
 * than alongside it, so the two cannot drift.
 *
 * Null when there is no spot to be a percentage of — the honest answer, and
 * the table says it has no rows rather than computing against zero.
 */
export function halfPctOf(window: PayoffWindow, spot: number): number | null {
  if (!(spot > 0)) return null
  return ((window.to - window.from) / 2 / spot) * 100
}
